import { filterValidLocalizationMessages } from './localizationMessages'

export const UI_LOCALIZATION_MAX_AGE_MS = 5 * 60 * 1000

/** Cache only successful loads and discard responses from an earlier session/locale. */
export function createUiLocalizationLoader(
  fetchMessages: (locale: string) => Promise<Record<string, string>>,
  referenceMessages: () => Record<string, unknown>,
  mergeMessages: (locale: string, messages: Record<string, string>) => void,
  isMerged: (locale: string, messages: Record<string, string>) => boolean,
) {
  let loaded: { locale: string, identity: unknown, loadedAt: number, messages: Record<string, string> } | undefined
  let pending: { locale: string, identity: unknown, promise: Promise<void> } | undefined
  let generation = 0

  return async (locale: string, identity: unknown) => {
    if (loaded?.locale === locale && loaded.identity === identity
      && Date.now() - loaded.loadedAt < UI_LOCALIZATION_MAX_AGE_MS) {
      if (pending) {
        generation++
        pending = undefined
      }
      // @nuxtjs/i18n merges the bundled locale file back over saved messages
      // whenever setLocale runs, even for the active locale.
      if (!isMerged(locale, loaded.messages))
        mergeMessages(locale, loaded.messages)
      return
    }
    if (pending?.locale === locale && pending.identity === identity)
      return pending.promise
    const requestGeneration = ++generation
    const promise = (async () => {
      try {
        const messages = await fetchMessages(locale)
        if (generation !== requestGeneration)
          return
        const validMessages = filterValidLocalizationMessages(messages, {
          referenceMessages: referenceMessages(),
        })
        mergeMessages(locale, validMessages)
        loaded = { locale, identity, loadedAt: Date.now(), messages: validMessages }
      }
      catch {
        // Keep the bundled messages, but retry on the next authenticated setup/navigation.
      }
      finally {
        if (generation === requestGeneration)
          pending = undefined
      }
    })()
    pending = { locale, identity, promise }
    await promise
  }
}
